/**
 * User-visible Library lifecycle status mapping.
 * Canonical backend lifecycle state is authoritative when present.
 */

import type { LibraryDocumentCanonicalState } from "../api/cognitoryLibraryClient";

export type LibraryIngestionUserVisibleStatus =
  | "processing"
  | "ready"
  | "processing_failed";

export const LIBRARY_INGESTION_PROCESSING_LABEL = "Processing…" as const;
export const LIBRARY_INGESTION_READY_LABEL = "Ready" as const;
export const LIBRARY_INGESTION_FAILED_LABEL = "Processing failed" as const;
export const LIBRARY_INGESTION_STILL_PROCESSING_LABEL =
  "Still processing. You can leave this page and check back later." as const;
export const LIBRARY_CANONICAL_INDEXED_NOT_SERVING_LABEL =
  "Indexed — not in library" as const;
export const LIBRARY_CANONICAL_BUILDING_LIBRARY_LABEL =
  "Building library…" as const;
export const LIBRARY_CANONICAL_ACTIVATING_LABEL = "Activating…" as const;
export const LIBRARY_CANONICAL_REMOVING_LABEL = "Removing…" as const;

const READY_VERSION_STATUSES = new Set([
  "vector_indexed",
  "indexed",
  "verified",
]);

const PROCESSING_VERSION_STATUSES = new Set([
  "source_recorded",
  "structure_chunked",
  "parsing",
  "chunked",
  "embedded",
]);

const CANONICAL_STATE_LABELS: Record<
  LibraryDocumentCanonicalState,
  string | null
> = {
  processing: LIBRARY_INGESTION_PROCESSING_LABEL,
  indexed_not_serving: LIBRARY_CANONICAL_INDEXED_NOT_SERVING_LABEL,
  building_library: LIBRARY_CANONICAL_BUILDING_LIBRARY_LABEL,
  activating: LIBRARY_CANONICAL_ACTIVATING_LABEL,
  answerable: LIBRARY_INGESTION_READY_LABEL,
  terminal_failed: LIBRARY_INGESTION_FAILED_LABEL,
  deleting: LIBRARY_CANONICAL_REMOVING_LABEL,
  deleted: null,
};

/** Legacy fallback when canonicalState is absent from the API response. */
export function mapVersionStatusToUserVisible(
  versionStatus: string | null,
): LibraryIngestionUserVisibleStatus | null {
  if (!versionStatus) {
    return null;
  }
  if (versionStatus === "failed") {
    return "processing_failed";
  }
  if (READY_VERSION_STATUSES.has(versionStatus)) {
    return "ready";
  }
  if (PROCESSING_VERSION_STATUSES.has(versionStatus)) {
    return "processing";
  }
  return null;
}

export function mapCanonicalStateToUserVisible(
  canonicalState: LibraryDocumentCanonicalState,
): LibraryIngestionUserVisibleStatus {
  if (canonicalState === "answerable") {
    return "ready";
  }
  if (canonicalState === "terminal_failed") {
    return "processing_failed";
  }
  return "processing";
}

export function isTerminalCanonicalState(
  canonicalState: LibraryDocumentCanonicalState,
): boolean {
  return (
    canonicalState === "answerable" || canonicalState === "terminal_failed"
  );
}

export function isTerminalIngestionUserVisibleStatus(
  status: LibraryIngestionUserVisibleStatus,
): boolean {
  return status === "ready" || status === "processing_failed";
}

export function resolveLibraryItemUserVisibleStatus(input: {
  canonicalState?: LibraryDocumentCanonicalState | undefined;
  versionStatus: string | null;
}): LibraryIngestionUserVisibleStatus {
  if (input.canonicalState) {
    return mapCanonicalStateToUserVisible(input.canonicalState);
  }
  return mapVersionStatusToUserVisible(input.versionStatus) ?? "processing";
}

export function resolveLibraryItemUserVisibleLabel(input: {
  canonicalState?: LibraryDocumentCanonicalState | undefined;
  versionStatus: string | null;
  timedOut?: boolean;
}): string {
  if (input.timedOut) {
    return LIBRARY_INGESTION_STILL_PROCESSING_LABEL;
  }
  if (input.canonicalState) {
    const canonicalLabel = CANONICAL_STATE_LABELS[input.canonicalState];
    if (canonicalLabel) {
      return canonicalLabel;
    }
    return LIBRARY_INGESTION_PROCESSING_LABEL;
  }
  const mapped = mapVersionStatusToUserVisible(input.versionStatus);
  if (mapped === "ready") {
    return LIBRARY_INGESTION_READY_LABEL;
  }
  if (mapped === "processing_failed") {
    return LIBRARY_INGESTION_FAILED_LABEL;
  }
  return LIBRARY_INGESTION_PROCESSING_LABEL;
}

export function resolveLibraryItemStatusClassName(input: {
  canonicalState?: LibraryDocumentCanonicalState | undefined;
  versionStatus: string | null;
  timedOut?: boolean;
}): string {
  if (input.timedOut) {
    return "cognitory-library-item-status cognitory-library-item-status--neutral";
  }
  const bucket = resolveLibraryItemUserVisibleStatus({
    canonicalState: input.canonicalState,
    versionStatus: input.versionStatus,
  });
  if (bucket === "ready") {
    return "cognitory-library-item-status cognitory-library-item-status--ready";
  }
  if (bucket === "processing_failed") {
    return "cognitory-library-item-status cognitory-library-item-status--failed";
  }
  return "cognitory-library-item-status cognitory-library-item-status--processing";
}

/** Legacy fallback when canonicalState is absent. */
export function isIngestionStatusStillProcessing(
  versionStatus: string | null,
): boolean {
  const mapped = mapVersionStatusToUserVisible(versionStatus);
  return mapped === "processing" || mapped === null;
}

export function isLibraryDocumentIngestionStillProcessing(input: {
  canonicalState?: LibraryDocumentCanonicalState | undefined;
  versionStatus: string | null;
}): boolean {
  if (input.canonicalState) {
    return !isTerminalCanonicalState(input.canonicalState);
  }
  return isIngestionStatusStillProcessing(input.versionStatus);
}

export function isLibraryDocumentIngestionTerminal(input: {
  canonicalState?: LibraryDocumentCanonicalState | undefined;
  versionStatus: string | null;
}): boolean {
  if (input.canonicalState) {
    return isTerminalCanonicalState(input.canonicalState);
  }
  const mapped = mapVersionStatusToUserVisible(input.versionStatus);
  return mapped !== null && isTerminalIngestionUserVisibleStatus(mapped);
}
